import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { ParticleExplosion } from '../components/feedback/ParticleExplosion';
import { FeedbackCompletion } from '../components/feedback/FeedbackCompletion';
import { FeedbackQuestionRenderer } from '../components/feedback/FeedbackQuestionRenderer';
import { logger } from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  { id: 'usage_frequency', type: 'cards' },
  { id: 'value_score', type: 'slider' },
  { id: 'features_used', type: 'multi_select' },
  { id: 'likes_most', type: 'textarea' },
  { id: 'frustrations', type: 'textarea_red' },
  { id: 'missing_feature', type: 'textarea' },
  { id: 'would_miss', type: 'stacked' },
  { id: 'would_recommend', type: 'stacked' },
  { id: 'user_type', type: 'cards' },
  { id: 'additional_comments', type: 'textarea_final' },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    const saved = localStorage.getItem('sendpi_feedback_step');
    return saved ? parseInt(saved) : 0;
  });
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem('sendpi_feedback_answers');
    return saved ? JSON.parse(saved) : {};
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    localStorage.setItem('sendpi_feedback_step', step);
    localStorage.setItem('sendpi_feedback_answers', JSON.stringify(answers));
  }, [step, answers]);

  const setAnswer = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));
  const canGoNext = useCallback(() => {
    const q = QUESTIONS[step];
    const a = answers[q.id];
    if (q.id === 'features_used') return a && a.length > 0;
    if (q.id === 'likes_most' || q.id === 'frustrations') return a && a.length >= 20;
    if (q.id === 'additional_comments') return true;
    return !!a;
  }, [step, answers]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          usage_frequency: answers.usage_frequency || '',
          value_score: answers.value_score || 5,
          features_used: answers.features_used || [],
          likes_most: answers.likes_most || '',
          frustrations: answers.frustrations || '',
          missing_feature: answers.missing_feature || '',
          would_miss: answers.would_miss || '',
          would_recommend: answers.would_recommend || '',
          user_type: answers.user_type || '',
          additional_comments: answers.additional_comments || ''
        })
      });
      const data = await res.json();
      setStats(data);
      setShowParticles(true);
      setTimeout(() => setSubmitted(true), 1500);
      localStorage.removeItem('sendpi_feedback_step');
      localStorage.removeItem('sendpi_feedback_answers');
    } catch (err) {
      logger.error('Feedback submission failed:', err);
    } finally {
      setSubmitting(false);
    }
  }, [answers]);

  const next = useCallback(() => {
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else handleSubmit();
  }, [step, handleSubmit]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && canGoNext()) next();
    if (e.key === 'Escape') {
      if (window.confirm('Are you sure you want to leave? Your progress is saved.')) navigate('/dashboard');
    }
  }, [canGoNext, navigate, next]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (submitted) {
    return <FeedbackCompletion answers={answers} stats={stats} showParticles={showParticles} />;
  }

  return (
    <div className="min-h-screen bg-[#060C18] flex flex-col">
      <ParticleExplosion active={showParticles} />
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[#060C18]/80 backdrop-blur-md p-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="text-white/40 hover:text-white">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <span className="text-white/40 text-sm ml-auto">Question {step + 1} of {QUESTIONS.length}</span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full"
              animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
              transition={{ type: 'spring', stiffness: 200, damping: 30 }}
            />
          </div>
        </div>
      </div>

      {/* Question area */}
      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-8 text-center">
        <AnimatePresence mode="wait">
          <FeedbackQuestionRenderer
            question={QUESTIONS[step]}
            answers={answers}
            setAnswer={setAnswer}
            step={step}
            setStep={setStep}
            canGoNext={canGoNext}
            next={next}
            handleSubmit={handleSubmit}
            submitting={submitting}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}
